import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { fadeIn } from '@/utils/ui';

import BookingCard from '@/components/booking-card';
import Header from '@/components/elements/header';
import JourneyProgress from '@/components/JourneyProgress';
import Map from '@/components/map';

import { sortUnitTypes } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { MapContextProvider } from '@/features/rapidstor/providers/MapProvider';

import classes from '../scss/screens/book-unit-map.module.scss';

type MobileDisplay = 'Map' | 'Booking';

export default function BookUnitMap() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { unitData, setUnitData, descriptorList } = useContext(JourneyContext);

    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [selectedUnitType, setSelectedUnitType] = useState<string>('');
    const [selectedDeal, setSelectedDeal] = useState<string | null>(null);

    const [currentDisplay, setCurrentDisplay] = useState<MobileDisplay>('Booking');

    useEffect(() => {
        fadeIn();
    }, []);

    useEffect(() => {
        if (descriptorList && unitData) {
            descriptorList.map((d, index) => {
                if (d._id === unitData.descriptor._id) {
                    setSelectedIndex(index);
                    setSelectedUnitType(sortUnitTypes(d.unitTypes, location)[0]._id);
                }
            });
        } else {
            navigate('/');
        }
    }, [descriptorList, location, navigate, unitData]);

    const selectedDescriptor = useMemo(() => {
        if (selectedIndex === -1) {
            return undefined;
        }
        return descriptorList[selectedIndex];
    }, [selectedIndex, descriptorList]);

    const journeyStepValue = useMemo(() => {
        return window.rapidstor_settings.app_config.pages.storageType ? 3 : 2;
    }, []);

    const availableDeals = useMemo(() => {
        const descriptor = unitData?.descriptors.find((d) => {
            return d.unitTypes.find((ut) => ut._id === selectedUnitType);
        });

        if (!descriptor) {
            return [];
        }

        const deals = descriptor.unitTypes
            .filter((ut) => ut._id === selectedUnitType)
            .map((ut) => ut.deals)
            .flat();

        return deals;
    }, [selectedUnitType, unitData?.descriptors]);

    const changeDescriptorSize = (newIndex: number): UnitType | null => {
        if (!unitData) {
            return null;
        }

        if (newIndex < 0 || newIndex >= descriptorList.length) {
            return null;
        }

        setSelectedIndex(newIndex);
        const newUnitType = sortUnitTypes(descriptorList[newIndex].unitTypes, location)[0];
        setSelectedDeal(null);
        setUnitData({
            ...unitData,
            descriptor: descriptorList[newIndex],
            unitType: newUnitType,
        });

        return newUnitType
    }

    useEffect(() => {
        if (!selectedDescriptor || !selectedUnitType) {
            return;
        }

        const unitType = selectedDescriptor.unitTypes.find(
            (ut) => ut._id === selectedUnitType
        );

        if (!unitType) {
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!unitData) {
        return null;
    }

    return (
        <div className={`${classes.container}`}>
            <JourneyProgress stepsType='regular' mobile step={journeyStepValue} />
            <div>
                <Header level={1} className={`${classes.title} ${classes.desktop}`}>
                    <span>{t('map:select_unit')}</span>
                </Header>
            </div>
            <JourneyProgress stepsType='regular' desktop step={journeyStepValue} />

            <MapContextProvider>
                <div className={`${classes.display_selector}`}>
                    <button
                        className={`${classes.display_selector_button} ${currentDisplay === 'Map' ? classes.active : ''
                            }`}
                        onClick={() => setCurrentDisplay('Map')}
                    >
                        {t('map:map')}
                    </button>
                    <button
                        className={`${classes.display_selector_button} ${currentDisplay === 'Booking' ? classes.active : ''
                            }`}
                        onClick={() => setCurrentDisplay('Booking')}
                    >
                        {t('map:booking')}
                    </button>
                </div>

                <div className={`${classes.row}`}>
                    <div className={`${classes.col} ${currentDisplay === 'Map' ? classes.visible : classes.hidden} ${currentDisplay === 'Map' ? classes.zAbove : classes.zBelow}`}>
                        <Map />
                    </div>
                    <div className={`${classes.col} ${currentDisplay === 'Booking' ? classes.visible : classes.hidden} ${currentDisplay === 'Booking' ? classes.zAbove : classes.zBelow}`}>
                        <div className={`${currentDisplay === 'Booking' ? classes.show : classes.hide}`}>
                            {selectedDescriptor && (
                                <BookingCard
                                    descriptor={selectedDescriptor}
                                    unitType={
                                        sortUnitTypes(
                                            selectedDescriptor.unitTypes,
                                            location
                                        )[0]
                                    }
                                    selectedUnitType={selectedUnitType}
                                    deals={availableDeals}
                                    selectedIndex={selectedIndex}
                                    hideSmallerButton={selectedIndex === 0}
                                    hideLargerButton={
                                        selectedIndex === unitData.descriptors.length - 1
                                    }
                                    setSelectedUnitType={setSelectedUnitType}
                                    changeDescriptorSize={changeDescriptorSize}
                                    selectedDeal={selectedDeal}
                                    setSelectedDeal={setSelectedDeal}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </MapContextProvider>
        </div>
    );
}
